import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { PenLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getStudioOverview } from "@/lib/writer.functions";

export const Route = createFileRoute("/_authenticated/studio/")({
  head: () => ({
    meta: [
      { title: "Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Your private Quibblies writing workspace: publications, drafts, submissions, analytics and reader support in one place.",
      },
      { property: "og:title", content: "Creator Studio — Quibblies" },
      { property: "og:description", content: "Your private Quibblies writing workspace." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: StudioOverview,
});

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function StudioOverview() {
  const fetchOverview = useServerFn(getStudioOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["studio-overview"],
    queryFn: () => fetchOverview(),
  });

  if (isLoading || !data) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="h-24 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const money = `£${(data.supportCents / 100).toFixed(2)}`;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="font-display text-xl text-foreground">Your publishing pipeline</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Published" value={data.published} />
          <Stat label="Drafts" value={data.drafts} />
          <Stat label="Pending review" value={data.pending} />
          <Stat label="Changes requested" value={data.changesRequested} />
          <Stat label="Approved, awaiting release" value={data.approved} />
          <Stat label="Rejected" value={data.rejected} />
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl text-foreground">How your writing is doing</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Total views" value={data.views} />
          <Stat label="Likes" value={data.likes} />
          <Stat label="Bookmarks" value={data.bookmarks} />
          <Stat label="Comments" value={data.comments} />
          <Stat label="Followers" value={data.followers} />
          <Stat label="Support received" value={money} hint="Successful contributions only" />
        </div>
      </section>

      <section className="rounded-3xl border border-primary/30 bg-primary/5 p-6">
        <h2 className="font-display text-xl text-foreground">Ready to write something new?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Drafts stay private until you submit them. Every submission is reviewed before it
          publishes.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild variant="hero" size="pill">
            <Link to="/write">
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Create publication
            </Link>
          </Button>
          <Button asChild variant="soft" size="pill">
            <Link to="/studio/submissions">Review feedback</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
